<script>
  import { onMount, tick } from "svelte";
  import CloseButton from "./CloseButton.svelte";
  import { makeEmbedUrl } from "./utils.js";
  export let src;
  export let onClose;
  let video; // bind:video
  let isIframe = false;
  let isYoutube = false;
  let isVimeo = false;
  let videoContainer;
  let youtubePlayer;
  let videoId = '';
  let uniqueId = `video-${Math.random().toString(36).substr(2, 9)}`;
  let videoAspectRatio = null;
  let videoContainerElement;
  let originalSrc = src; // Track original URL for oEmbed API
  let processedSrc = makeEmbedUrl(src, true) || src; // Processed URL for iframe embedding (autoplay enabled)

  // Update CSS custom properties when aspect ratio changes
  $: if (videoAspectRatio && videoContainerElement) {
    const root = videoContainerElement;
    if (root) {
      // Calculate dimensions based on actual video aspect ratio
      const aspectRatio = videoAspectRatio;
      
      // Clamp aspect ratio to reasonable bounds (0.5 to 3.0)
      const clampedRatio = Math.max(0.5, Math.min(3.0, aspectRatio));
      
      // Set CSS custom properties for dynamic sizing
      root.style.setProperty('--video-aspect-ratio', clampedRatio);
      root.style.setProperty('--video-width-ratio', clampedRatio > 1 ? '100vw' : `${100 * clampedRatio}vh`);
      root.style.setProperty('--video-height-ratio', clampedRatio > 1 ? `${100 / clampedRatio}vw` : '100vh');
      
    }
  }

  async function getVideoAspectRatio(videoId) {
    try {
      // Use the original URL for oEmbed API to get correct aspect ratio
      const oEmbedUrl = `https://www.youtube.com/oembed?url=${originalSrc}&format=json`;
      
      const response = await fetch(oEmbedUrl);
      const data = await response.json();
      
      const width = data.width;
      const height = data.height;
      const aspectRatio = width / height;
      
      
      videoAspectRatio = aspectRatio;
      return { width, height, aspectRatio, title: data.title, author: data.author_name };
    } catch (error) {
      console.error('Error fetching video aspect ratio:', error);
      return null;
    }
  }

  function loadYouTubeAPI() {
    return new Promise((resolve) => {
      if (!window.YT) { // Only load if not already loaded
        const tag = document.createElement('script');
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        // Set up the callback before loading the API
        window.onYouTubeIframeAPIReady = () => {
          resolve();
        };
      } else {
        resolve();
      }
    });
  }

  /**
   * Extract YouTube player parameters from URL and build playerVars object
   * @param {string} url - The processed YouTube embed URL
   * @returns {Object} playerVars object for YouTube IFrame API
   */
  function getPlayerVars(url) {
    // Default playerVars
    const playerVars = {
      // Add parameters to minimize tracking and CORS issues
      enablejsapi: 1,
      origin: window.location.origin,
      widget_referrer: window.location.origin,
      autoplay: 1,
      rel: 0,           // Don't show related videos
      modestbranding: 1 // Minimal YouTube branding
    };

    try {
      const urlObj = new URL(url);
      const params = urlObj.searchParams;

      // Extract start time (in seconds)
      const start = params.get('start');
      if (start) {
        const startSeconds = parseInt(start, 10);
        if (!isNaN(startSeconds) && startSeconds > 0) {
          playerVars.start = startSeconds;
        }
      }

      // Extract end time (in seconds)
      const end = params.get('end');
      if (end) {
        const endSeconds = parseInt(end, 10);
        if (!isNaN(endSeconds) && endSeconds > 0) {
          playerVars.end = endSeconds;
        }
      }

      // Extract playlist ID
      const list = params.get('list');
      if (list) {
        playerVars.list = list;
      }

      // Extract loop parameter (0 or 1)
      const loop = params.get('loop');
      if (loop !== null) {
        playerVars.loop = loop === '1' ? 1 : 0;
      }

      // Extract controls parameter (0, 1, or 2)
      const controls = params.get('controls');
      if (controls !== null) {
        const controlsValue = parseInt(controls, 10);
        if (!isNaN(controlsValue) && controlsValue >= 0 && controlsValue <= 2) {
          playerVars.controls = controlsValue;
        }
      }

      // Extract mute parameter (0 or 1)
      const mute = params.get('mute');
      if (mute !== null) {
        playerVars.mute = mute === '1' ? 1 : 0;
      }

      // Extract closed captions load policy (0 or 1)
      const ccLoadPolicy = params.get('cc_load_policy');
      if (ccLoadPolicy !== null) {
        playerVars.cc_load_policy = ccLoadPolicy === '1' ? 1 : 0;
      }

      // Extract language code for player interface
      const hl = params.get('hl');
      if (hl) {
        playerVars.hl = hl;
      }

      // Extract autoplay (override default if specified)
      const autoplay = params.get('autoplay');
      if (autoplay !== null) {
        playerVars.autoplay = autoplay === '1' ? 1 : 0;
      }

      // Extract rel (related videos) - override default if specified
      const rel = params.get('rel');
      if (rel !== null) {
        playerVars.rel = rel === '1' ? 1 : 0;
      }

      // Extract modestbranding - override default if specified
      const modestbranding = params.get('modestbranding');
      if (modestbranding !== null) {
        playerVars.modestbranding = modestbranding === '1' ? 1 : 0;
      }

    } catch (e) {
      // If URL parsing fails, return defaults
      console.warn('Failed to parse YouTube URL for playerVars:', e);
    }

    return playerVars;
  }

  async function setupYouTubePlayer() {
    // First ensure API is loaded
    await loadYouTubeAPI();
    
    // Then initialize the player
    if (!window.YT || !window.YT.Player) {
      setTimeout(setupYouTubePlayer, 100);
      return;
    }

    // Always clean up existing player before creating a new one
    if (youtubePlayer) {
      youtubePlayer.destroy();
      youtubePlayer = null;
    }

    if (!videoId) {
      console.warn('No video ID available');
      return;
    }
    
    // Ensure DOM element exists
    const element = document.getElementById(uniqueId);
    if (!element) {
      setTimeout(setupYouTubePlayer, 100);
      return;
    }
    
    
    // Get video aspect ratio from oEmbed API
    getVideoAspectRatio(videoId);
    
    // Build playerVars object from processed URL
    const playerVars = getPlayerVars(processedSrc);
    
    youtubePlayer = new YT.Player(uniqueId, {
      videoId,
      playerVars,
      events: {
        onReady: (event) => {
          // YouTube player is ready
        },
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.ENDED) {
            close();
          }
        },
        onError: (event) => {
          console.error('YouTube player error:', event.data);
        }
      }
    });
  }

  // Process the src URL when it changes
  $: if (src) {
    // Reset aspect ratio when src changes
    videoAspectRatio = null;
    
    // Reset CSS custom properties when src changes
    if (videoContainerElement) {
      videoContainerElement.style.removeProperty('--video-aspect-ratio');
      videoContainerElement.style.removeProperty('--video-width-ratio');
      videoContainerElement.style.removeProperty('--video-height-ratio');
    }
    
    originalSrc = src; // Store original URL
    processedSrc = makeEmbedUrl(src, true) || src; // Process for iframe embedding (autoplay enabled)
    
    if (/youtube/i.test(processedSrc)) {
      isYoutube = true;
      isIframe = true;
      // Extract video ID
      const match = processedSrc.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
      videoId = match ? match[1] : '';
      
      if (videoId) {
        setupYouTubePlayer();
      }
    } else if (/vimeo/i.test(processedSrc)) {
      isVimeo = true;
      isIframe = true;
    } else {
      isIframe = false;
    }
  }

  // Remove the onMount handler since we're handling initialization in loadYouTubeAPI
  onMount(() => {
    // Clean up YouTube player on component destroy
    return () => {
      if (youtubePlayer) {
        youtubePlayer.destroy();
        youtubePlayer = null;
      }
    };
  });

  async function focusVideo() {
    await tick();
    videoContainer.focus();
  }

  // TODO Implement focus trap
  // $: if (src) {
  //   focusVideo();
  // }

  import UA from "ua-parser-js";
  import { focusTrap } from "./utils";
  const ua = new UA();
  let mobile = ua.getResult().device["type"] == "mobile";

  function close() {
    if (youtubePlayer) {
      youtubePlayer.destroy();
      youtubePlayer = null;
    }
    
    // Reset CSS custom properties when closing
    if (videoContainerElement) {
      videoContainerElement.style.removeProperty('--video-aspect-ratio');
      videoContainerElement.style.removeProperty('--video-width-ratio');
      videoContainerElement.style.removeProperty('--video-height-ratio');
    }
    
    // Reset aspect ratio
    videoAspectRatio = null;
    
    src = false;
    if (onClose) {
      onClose();
    }
  }

  function fullscreen() {
    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.mozRequestFullScreen) {
      video.mozRequestFullScreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  }
</script>

<!-- /*____________ Markup ____________*/ -->
{#if src}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="background" on:click={close}></div>
  <div class="container" class:isMobile={mobile}>
    <header class="video-header">
      <CloseButton {close} useAbsolutePosition={false} />
    </header>
    <div class="video-container" bind:this={videoContainerElement}>
      {#if isIframe}
        {#if isYoutube}
          <!-- YouTube player will be created by the API -->
          <div id={uniqueId} bind:this={videoContainer}></div>
        {:else}
          <!-- Regular iframe for Vimeo and other services -->
          <iframe
            id={uniqueId}
            title="Video"
            src={processedSrc}
            allowfullscreen
            allow="autoplay"
            frameborder="0"
            bind:this={videoContainer}
          />
        {/if}
      {:else}
        <!-- svelte-ignore a11y-media-has-caption -->
        <video id={uniqueId} controls autoplay bind:this={video}>
          <source src={processedSrc} type="video/mp4" />
        </video>
      {/if}
    </div>
  </div>
{/if}

<!-- /*____________ CSS ____________*/ -->
<style>
  .background {
    background: #000000;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99101;
  }

  .container {
    display: grid;
    grid-template-rows: auto 1fr;
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    z-index: 99102;
  }

  .video-header {
    grid-row: 1;
    display: flex;
    justify-content: flex-end;
    padding: 0.5em;
  }

  .video-container {
    grid-row: 2;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
    margin: auto;
    overflow: hidden;
  }

  video,
  iframe,
  .video-container > div {
    /* Use dynamic aspect ratio if available, fallback to 16:9 */
    width: var(--video-width-ratio, 100vw);
    height: var(--video-height-ratio, 56.25vw);
    max-height: calc(100vh - 100px); /* close button height */
    max-width: 100vw;
    margin: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  /* Fallback for when aspect ratio is not available */
  .video-container:not([style*="--video-aspect-ratio"]) video,
  .video-container:not([style*="--video-aspect-ratio"]) iframe,
  .video-container:not([style*="--video-aspect-ratio"]) > div {
    width: 100vw;
    height: 56.25vw; /* height:width ratio = 9/16 = .5625  */
    max-width: 177.78vh; /* 16/9 = 1.778 */
  }

  /* landscape mobile */
  @media (orientation: landscape) {
    .isMobile .video-header {
      position: absolute;
      right: 0;
      top: 0;
      background: #000000;
      z-index: 999999;
      padding: 0.5em 0.75em 0.15em 0.75em;
    }

    .isMobile video,
    .isMobile iframe,
    .isMobile .video-container > div {
      /* On mobile landscape, prioritize full screen but respect aspect ratio */
      height: 100vh;
      width: var(--video-width-ratio, 100vw);
      max-width: 100vw;
      max-height: 100vh;
    }
  }
</style>
